
import {
  faGiftCard,
  faCreditCard,
  faGaugeHigh
} from '@fortawesome/pro-solid-svg-icons';
import moment from 'moment';
import React, { ReactElement, useState } from 'react';
import VoxelAlert, { VoxelAlertProps } from '../../../../components/voxel/Alert/VoxelAlert';
import VoxelLink from '../../../../components/voxel/Link/VoxelLink';
import { AppTierTypeGq } from '../../../../graphql/projects/models/AppTierTypeGq';
import { CurrentUserGq } from '../../../../graphql/users/models/CurrentUserGq';
import { TeamMenuChoices } from '../../../TeamPage/TeamPage';


export const alertLocalStorageKey = `closed-alerts`;

export function useGetAlertList(user: CurrentUserGq): ReactElement<VoxelAlertProps>[] {
  const now = moment();
  const [reloadState, setReloadState] = useState(''); // Hack to force a rerender

  // Set stored state (workSpace)
  const setCloseAlertWorkspace = (alertId: string) => {
    localStorage.removeItem(alertLocalStorageKey + '-' + alertId + '-' + user.id);
    localStorage.setItem(
      alertLocalStorageKey + '-' + alertId + '-' + user.id,
      now.valueOf().toString()
    );
    setReloadState(new Date().toString());
  };

  // Get stored state (workSpace)
  const getAlertFromStorageWorkspace = (alertId: string) => {
    const storedValue = localStorage.getItem(alertLocalStorageKey + '-' + alertId + '-' + user.id);
    if (storedValue === undefined) return;
    try {
      return moment(Number(storedValue));
    } catch {
      return undefined;
    }
  };

  // CREATE ALERTS
  let alertList: ReactElement<VoxelAlertProps>[] = [];

  // ALERT: No plan
  const noPlanShutOffAt = getAlertFromStorageWorkspace('noPlanAlert');
  if (
    user.team.appTier.tierType === AppTierTypeGq.NONE &&
    (!noPlanShutOffAt || now.diff(noPlanShutOffAt, 'hour') > 0)
  ) {
    alertList.push(
      <VoxelAlert
        id={'noPlanAlert'}
        onClose={() => setCloseAlertWorkspace('noPlanAlert')}
        hideClose={false}
        title="This workspace has no active plan"
        severity="error"
        fontAIconName={faCreditCard}
        size="sm"
        iconSize='xl'
        iconPosition="center"
        widthClass="pl-12 pr-16"
        roundedClass='rounded-none'
      >
        <div className='pr-8'>
          No active subscription or trial was found. Please checkout the{' '}
          <VoxelLink href={`/team/${TeamMenuChoices.Subscription}`} openInNewTab openInNewIcon>
            {' '}
            subscription page
          </VoxelLink>{' '}
          for more information.
        </div>
      </VoxelAlert>
    );
  }

  // ALERT: Free trial expiration
  const trialShutOffAt = getAlertFromStorageWorkspace('trialExpirationAlert');
  if (
    !!user.team.trialExpiresAt &&
    !user.team.eligibleForSponsoredSubscription &&
    !user.team.subscriptionStartsAt &&
    moment().utc().diff(user.team.trialExpiresAt, 'days') > -10 &&
    (!trialShutOffAt || now.diff(trialShutOffAt, 'day') > 0)
  ) {
    const time = moment.utc(user.team.trialExpiresAt);
    let title = time.isBefore()
      ? `Free trial expired ${time.fromNow()}`
      : `Free trial expires ${time.fromNow()}`;
    alertList.push(
      <VoxelAlert
        id={'trialExpirationAlert'}
        onClose={() => setCloseAlertWorkspace('trialExpirationAlert')}
        hideClose={false}
        title={title}
        severity="info"
        fontAIconName={faGiftCard}
        size="sm"
        iconSize='xl'
        iconPosition="center"
        widthClass="pl-12 pr-16"
        roundedClass='rounded-none'
      >
        <div className='pr-8'>
          Visit the{' '}
          <VoxelLink href={`/team/${TeamMenuChoices.Subscription}`} openInNewIcon openInNewTab>
            subscription page
          </VoxelLink>{' '}
          if you want to keep using Voxel after free trial has ended.
        </div>
      </VoxelAlert>
    );
  }

  // ALERT: Subscription end
  const subExpShutOffAt = getAlertFromStorageWorkspace('subscriptionExpirationAlert');
  if (
    !user.team.trialExpiresAt &&
    !!user.team.subscriptionExpiresAt &&
    now.diff(user.team.subscriptionExpiresAt, 'days') > -10 &&
    (!subExpShutOffAt || now.diff(subExpShutOffAt, 'day') > 0)
  ) {
    const time = moment.utc(user.team.trialExpiresAt);
    let title = time.isBefore()
      ? `Subscription expired ${time.fromNow()}`
      : `Subscription expires ${time.fromNow()}`;
    alertList.push(
      <VoxelAlert
        id={'subscriptionExpirationAlert'}
        onClose={() => setCloseAlertWorkspace('subscriptionExpirationAlert')}
        hideClose={false}
        title={title}
        severity="info"
        fontAIconName={faGiftCard}
        size="sm"
        iconSize='xl'
        iconPosition="center"
        widthClass="pl-12 pr-16"
        roundedClass='rounded-none'
      >
        <div className='pr-8'>
          Visit the{' '}
          <VoxelLink href={`/team/${TeamMenuChoices.Subscription}`} openInNewIcon openInNewTab>
            subscription page
          </VoxelLink>{' '}
          if you want to keep using Voxel after the subscription has ended.
        </div>
      </VoxelAlert>
    );
  }

  // ALERT: Capacity issues alert
  const usageShutOffAt = getAlertFromStorageWorkspace('usageAlert');
  const blockedAspects: string[] = [];
  if (user?.team?.blockMonthlyUseSearchVolumeKw) blockedAspects.push('Search volume');
  if (user?.team?.blockMonthlyUseRankKw) blockedAspects.push('Rank data');
  if (user?.team?.blockMonthlyUseTranslateKw) blockedAspects.push('Translations');
  if (user?.team?.blockMonthlyUseChatGptDrafts) blockedAspects.push('ChatGPT');
  if (user?.team?.blockMonthlyProxies) blockedAspects.push('Proxy usage');
  if (!!blockedAspects?.length && (!usageShutOffAt || now.diff(usageShutOffAt, 'day') > 0)) {
    alertList.push(
      <VoxelAlert
        id={'usageAlert'}
        onClose={() => setCloseAlertWorkspace('usageAlert')}
        hideClose={false}
        severity="error"
        fontAIconName={faGaugeHigh}
        title={'Usage exceeded'}
        size="sm"
        iconSize='xl'
        iconPosition="center"
        widthClass="pl-12 pr-16"
        roundedClass='rounded-none'
      >
        <div className='pr-8'>
          The account usage has exceeded its limit in the following areas: {blockedAspects.join(', ')}{' '}
          Visit the{' '}
          <VoxelLink href={`/team/${TeamMenuChoices.Usage}`} openInNewIcon openInNewTab>
            usage page
          </VoxelLink>{' '}
          for more information.
        </div>
      </VoxelAlert>
    );
  }

  if (moment.utc('2023-11-14').isAfter()) {
    alertList.push(
      <VoxelAlert
        id={'outage'}
        onClose={() => setCloseAlertWorkspace('outage')}
        hideClose={true}
        severity="warning"
        fontAIconName={faGaugeHigh}
        title={'Limited processing'}
        size="sm"
        iconSize='xl'
        iconPosition="center"
        widthClass="pl-12 pr-16"
        roundedClass='rounded-none'
      >
        <div className='pr-8'>
          Data processing will be delayed throughout the day. Voxel is experiencing issues with our
          data providers right now.
        </div>
      </VoxelAlert>
    );
  }

  return alertList;
}
